import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { MinistryAppointmentManagementService, MinistryAppointmentDto, CreateMinistryAppointmentDto, UpdateMinistryAppointmentDto } from '../../services/ministry-appointment-management.service';

/**
 * Componente para crear y editar encargos ministeriales
 */
@Component({
  selector: 'app-ministry-appointment-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PageHeaderComponent
  ],
  templateUrl: './ministry-appointment-editor.component.html',
  styleUrl: './ministry-appointment-editor.component.scss'
})
export class MinistryAppointmentEditorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  appointmentId: number | null = null;
  currentAppointment: MinistryAppointmentDto | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private service: MinistryAppointmentManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.appointmentId = +params['id'];
          this.isEditMode = true;
          this.loadAppointment();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa el formulario
   */
  private initializeForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]]
    });
  }

  /**
   * Carga el encargo para editar
   */
  private loadAppointment(): void {
    if (!this.appointmentId) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.appointmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (appointment: MinistryAppointmentDto) => {
          this.currentAppointment = appointment;
          this.form.patchValue({
            name: appointment.name
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading ministry appointment:', error);
          this.notificationService.error('Error al cargar el encargo ministerial');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Guarda el encargo ministerial (crear o actualizar)
   */
  saveAppointment(): void {
    if (!this.form.valid) {
      this.notificationService.warning('Por favor, completa todos los campos correctamente');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const formValue = this.form.value;

    if (this.isEditMode && this.appointmentId && this.currentAppointment) {
      // En modo edición, preservar isActive y sortOrder del encargo actual
      const updateDto: UpdateMinistryAppointmentDto = {
        ...formValue,
        isActive: this.currentAppointment.isActive,
        sortOrder: this.currentAppointment.sortOrder
      };
      this.updateAppointment(this.appointmentId, updateDto);
    } else {
      this.createAppointment(formValue);
    }
  }

  /**
   * Crea un nuevo encargo ministerial
   */
  private createAppointment(dto: CreateMinistryAppointmentDto): void {
    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Encargo ministerial creado exitosamente');
          this.router.navigate(['/configuration/ministry-appointments']);
        },
        error: (error: any) => {
          console.error('Error creating ministry appointment:', error);
          const message = (error.error as any)?.message || 'Error al crear el encargo ministerial';
          this.notificationService.error(message);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Actualiza un encargo ministerial existente
   */
  private updateAppointment(id: number, dto: UpdateMinistryAppointmentDto): void {
    this.service.update(id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Encargo ministerial actualizado exitosamente');
          this.router.navigate(['/configuration/ministry-appointments']);
        },
        error: (error: any) => {
          console.error('Error updating ministry appointment:', error);
          const message = (error.error as any)?.message || 'Error al actualizar el encargo ministerial';
          this.notificationService.error(message);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Cancela la edición y regresa al listado
   */
  cancel(): void {
    this.router.navigate(['/configuration/ministry-appointments']);
  }
}
